import { useEffect, useRef, useState } from 'react';
import type { ActiveInsight } from './api';
import { chartInsightDataKey, refreshChartInsight, type ChartInsightRefreshRequest } from './chart-insight-refresh';
import { currentChartInsight } from './chart-insight-summary';
import { userFacingAnalyticsError } from '../lib/user-facing';

export function useChartInsightRefresh({ result, insights, enabled, canPersist, onRefresh, ...target }: Omit<ChartInsightRefreshRequest, 'result'> & {
  result?: ChartInsightRefreshRequest['result']; insights: ActiveInsight[]; enabled: boolean; canPersist: boolean; onRefresh: () => Promise<void>;
}) {
  const [status, setStatus] = useState<{ refreshing: boolean; error?: string }>({ refreshing: false });
  const callback = useRef(onRefresh);
  callback.current = onRefresh;
  const key = result ? chartInsightDataKey(result) : '';
  const existing = insights.filter(item => item.revision.source !== 'manual');
  const needed = enabled && canPersist && existing.length > 0 && !!result && !existing.some(item => currentChartInsight(result, item));
  const targetKey = JSON.stringify(target);

  useEffect(() => {
    let live = true;
    if (!needed || !result) { setStatus({ refreshing: false }); return; }
    setStatus({ refreshing: true });
    // Let rapid filters settle before creating a saved revision.
    const timer = window.setTimeout(() => {
      void refreshChartInsight({ ...target, result }, () => callback.current(), () => live)
        .then(() => { if (live) setStatus({ refreshing: false }); })
        .catch(cause => { if (live) setStatus({ refreshing: false, error: userFacingAnalyticsError(cause, 'The insight could not be refreshed.') }); });
    }, 500);
    return () => { live = false; window.clearTimeout(timer); };
  }, [key, targetKey, needed]);

  return status;
}
